/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React from 'react';
import Media from 'react-media';
import {
    object, string, shape, arrayOf, number, func,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Grid from '@material-ui/core/Grid';
import ReactHTMLParser from 'react-html-parser';
import ConnectedGraphqlProductSkus from '../../GraphqlPDPSkus/GraphqlProductSkus';

const styles = (theme) => ({
    productImage: {
        width: '100%',
        height: 'auto',
        borderStyle: 'solid',
        borderColor: 'rgba(0, 0, 0, 0.3)',
        borderWidth: '1px',
    },
    productImageDesktop: {
        [theme.breakpoints.down(959)]: {
            marginLeft: '10px',
        },
    },
    prodName: {
        fontSize: '18px',
        fontWeight: '800',
        marginBottom: '5px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.25em',
            fontWeight: '600',
            marginBottom: '10px',
        },
    },
    prodAvailability: {
        color: theme.palette.cms?.skuAvailableToShipTextColor || '#999999',
        fontSize: '11px',
        [theme.breakpoints.down('sm')]: {
            color: theme.palette.cms?.cyoDeliveryMsg || '#525252',
            padding: '5px 0 10px 0',
            fontSize: '0.9em',
            fontWeight: '600',
        },
    },
    partNumber: {
        fontSize: '14px',
    },
    prodPrice: {
        fontSize: '18px',
        fontWeight: '700',
        [theme.breakpoints.down('sm')]: {
            color: theme.palette.colorSecondary || '#600',
        },
    },
    mobileProdDetails: {
        padding: '0 10px 0 10px',
    },
    shortDescription: {
        [theme.breakpoints.down(959)]: {
            paddingRight: '10px',
        },
    },
});

const CYOProductDetails = ({
    classes,
    data,
    selectedSku,
    skuData,
    content,
    presentation_family,
    handleSkuChange,
    location,
    brand,

}) => {
    let skuConfig = {};
    // let deliveryData = {};
    // let passportMemberImg = '';
    let productUrl = '';

    if (content?.entries?.[0]) {
        skuConfig = content.entries[0].sku_section;
        // deliveryData = content.entries[0].delivery_section;
        // passportMemberImg = content.entries[0].passport_banner_section?.member_image;
        productUrl = content.entries[0].url;
    }

    const buildImgPath = (path, name) => `${path + name}x.jpg`;
    const {
        availability,
        name,
        partNumber,
        shortDescription,
    } = data;
    const imageName = data.image?.name ?? '';
    const imagePath = selectedSku?.image?.path ?? '';
    const heroImage = buildImgPath(imagePath, imageName) ?? '';
    const price = skuData?.map((sku) => sku.prices[0].value);
    const deliveryMessage = availability?.deliveryMessage;

    const desktopProductDetails = () => (
        <Grid container spacing={4}>
            <Grid item md={4} lg={4} sm={4}>
                <img src={heroImage} alt={data.name} className={`${classes.productImage} ${classes.productImageDesktop}`} />
            </Grid>
            <Grid item md={8} lg={8} sm={8}>
                <h1 className={classes.prodName}>{ReactHTMLParser(name) || ''}</h1>
                {(skuData?.length > 1) ? (
                    <ConnectedGraphqlProductSkus
                        skuData={skuData}
                        config={skuConfig}
                        handleSkuChange={handleSkuChange}
                        selectedSku={selectedSku}
                        presentation_family={presentation_family}
                        product={data}
                        productUrl={productUrl}
                        location={location}
                        brand={brand}
                    />
                ) : (
                    <>
                        <div className={classes.prodAvailability}>{deliveryMessage || ''}</div>
                        <div className={classes.partNumber}>{partNumber || ''}</div>
                        <div className={classes.prodPrice}>${price || ''}</div>
                    </>
                )}
                <div className={classes.shortDescription}>{ReactHTMLParser(shortDescription) || ''}</div>
            </Grid>
        </Grid>
    );
    const mobileProductDetails = () => (
        <Grid container>
            <Grid item xs={12}>
                <img src={heroImage} alt={data.name} className={classes.productImage} />
            </Grid>
            <Grid item xs={12} className={classes.mobileProdDetails}>
                <h1 className={classes.prodName}>{ReactHTMLParser(name) || ''}</h1>
                {(skuData?.length > 1) ? (
                    <ConnectedGraphqlProductSkus
                        skuData={skuData}
                        config={skuConfig}
                        handleSkuChange={handleSkuChange}
                        selectedSku={selectedSku}
                        presentation_family={presentation_family}
                        product={data}
                        productUrl={productUrl}
                        location={location}
                        brand={brand}
                    />
                ) : (
                    <>
                        <div className={classes.partNumber}>{partNumber.split('-')[2] || ''}</div>
                        <div className={classes.prodPrice}>${price || ''}</div>
                        <div className={classes.prodAvailability}>{deliveryMessage || ''}</div>
                    </>
                )}
            </Grid>
        </Grid>
    );

    return (
        <Media queries={{
            mobile: '(min-width: 320px) and (max-width: 767px)',
            desktop: '(min-width: 768px)',
        }}
        >
            {(matches) => (
                <>
                    {matches.mobile ? mobileProductDetails() : desktopProductDetails()}
                </>
            )}
        </Media>
    );
};

CYOProductDetails.propTypes = {
    classes: object.isRequired,
    data: shape({
        name: string.isRequired,
        partNumber: string.isRequired,
        shortDescription: string.isRequired,
        image: shape({
            name: string.isRequired,
            path: string.isRequired,
        }).isRequired,
        availability: shape({
            deliveryMessage: string.isRequired,
            productDeliveryType: string.isRequired,
        }).isRequired,
    }).isRequired,
    selectedSku: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ).isRequired,
    brand: shape({
        domain: string.isRequired,
    }).isRequired,
    content: shape({
        entries: arrayOf(shape({
            sku_section: object.isRequired,
            url: string.isRequired,
        })),
    }).isRequired,
    presentation_family: string.isRequired,
    handleSkuChange: func.isRequired,
    location: object.isRequired,
};

export default withStyles(styles)(CYOProductDetails);
